import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";

const formatNumber = (value, isMoney = false) => {
  if (value === null || value === undefined) return isMoney ? "$0" : "0";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  const formatted = num.toLocaleString("es-EC");
  return isMoney ? `$${formatted}` : formatted;
};

const accentMap = {
  blue: { bg: "#e0ecff", fg: "#1d4ed8" },
  violet: { bg: "#ebe9ff", fg: "#6d28d9" },
  green: { bg: "#e5f8f2", fg: "#0f766e" },
  amber: { bg: "#fff4e0", fg: "#c2410c" },
  aqua: { bg: "#e0f7ff", fg: "#0ea5e9" },
  coral: { bg: "#ffe7e2", fg: "#dc2626" },
};

const dayLabel = (dia, fallbackIndex = 0) => {
  const names = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];
  const d = dia ? new Date(dia) : null;
  if (d && !Number.isNaN(d.getTime())) {
    return names[d.getDay()];
  }
  return names[fallbackIndex % names.length];
};

const formatShortDate = (dia, fallbackIndex = 0) => {
  const d = dia ? new Date(dia) : null;
  if (d && !Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}`;
  }
  return dayLabel(dia, fallbackIndex);
};

const monthLabels = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const toMonthLabel = (value) => {
  const idx = Number(value) - 1;
  return monthLabels[idx] || value;
};

const buildDefaultFilters = (base = {}) => ({
  centro_id: base.centro_id || "",
  curso_id: base.curso_id || "",
  promocion_id: base.promocion_id || "",
  cobranzaYear: "all",
  cobranzaMonth: "all",
  metodoYear: "all",
  metodoMonth: "all",
});

export default function FinancialTab({
  financiero = {},
  centros = [],
  cursos = [],
  promociones = [],
  globalFilters = {},
}) {
  const kpis = financiero.kpis || [];
  const facturas = financiero.facturas || [];
  const metodos = useMemo(() => financiero.metodoPago || [], [financiero.metodoPago]);
  const cobranzaDia = useMemo(() => financiero.cobranzaDia || [], [financiero.cobranzaDia]);
  const cobranzaMonthly = useMemo(() => financiero.cobranzaMonthly || [], [financiero.cobranzaMonthly]);
  const metodoPagoMonthly = useMemo(() => financiero.metodoPagoMonthly || [], [financiero.metodoPagoMonthly]);
  const [filters, setFilters] = useState(buildDefaultFilters(globalFilters));
  const [draftFilters, setDraftFilters] = useState(buildDefaultFilters(globalFilters));
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      centro_id: globalFilters.centro_id || "",
      curso_id: globalFilters.curso_id || "",
      promocion_id: globalFilters.promocion_id || "",
    }));
    setDraftFilters((prev) => ({
      ...prev,
      centro_id: globalFilters.centro_id || "",
      curso_id: globalFilters.curso_id || "",
      promocion_id: globalFilters.promocion_id || "",
    }));
  }, [globalFilters.centro_id, globalFilters.curso_id, globalFilters.promocion_id]);


  const toggleDropdown = (section) => {
    setDraftFilters(filters);
    setOpenDropdown((prev) => (prev === section ? null : section));
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    closeDropdown();
  };

  const resetDraftFilters = () => {
    setDraftFilters(buildDefaultFilters());
  };

  const cobranzaPeriod = useMemo(
    () =>
      filters.cobranzaYear !== "all" && filters.cobranzaMonth !== "all"
        ? `${filters.cobranzaYear}-${filters.cobranzaMonth}`
        : null,
    [filters.cobranzaYear, filters.cobranzaMonth]
  );

  const cobranzaSource = useMemo(() => {
    if (cobranzaPeriod) {
      return (cobranzaMonthly || []).filter((row) => row.period === cobranzaPeriod);
    }
    if (filters.cobranzaYear !== "all") {
      const year = String(filters.cobranzaYear);
      return (cobranzaMonthly || []).filter((row) => (row.period || "").startsWith(`${year}-`));
    }
    return cobranzaDia || [];
  }, [cobranzaPeriod, filters.cobranzaYear, cobranzaMonthly, cobranzaDia]);

  const metodoPeriod = useMemo(
    () =>
      filters.metodoYear !== "all" && filters.metodoMonth !== "all"
        ? `${filters.metodoYear}-${filters.metodoMonth}`
        : null,
    [filters.metodoYear, filters.metodoMonth]
  );

  const metodosSource = useMemo(() => {
    if (metodoPeriod) {
      return (metodoPagoMonthly || []).filter((row) => row.period === metodoPeriod);
    }
    if (filters.metodoYear !== "all") {
      const year = String(filters.metodoYear);
      return (metodoPagoMonthly || []).filter((row) => (row.period || "").startsWith(`${year}-`));
    }
    return metodos || [];
  }, [metodoPeriod, filters.metodoYear, metodoPagoMonthly, metodos]);

  const pieTotal = useMemo(() => {
    const matchIds = (row) => {
      const matchCentro = !filters.centro_id || String(row.centro_id || "") === String(filters.centro_id);
      const matchCurso = !filters.curso_id || String(row.curso_id || "") === String(filters.curso_id);
      const matchPromo = !filters.promocion_id || String(row.promocion_id || "") === String(filters.promocion_id);
      return matchCentro && matchCurso && matchPromo;
    };
    return metodosSource.filter(matchIds).reduce((acc, m) => acc + Number(m.value || m.total || 0), 0);
  }, [metodosSource, filters.centro_id, filters.curso_id, filters.promocion_id]);

  const pieData = useMemo(() => {
    const matchIds = (row) => {
      const matchCentro = !filters.centro_id || String(row.centro_id || "") === String(filters.centro_id);
      const matchCurso = !filters.curso_id || String(row.curso_id || "") === String(filters.curso_id);
      const matchPromo = !filters.promocion_id || String(row.promocion_id || "") === String(filters.promocion_id);
      return matchCentro && matchCurso && matchPromo;
    };
    if (!pieTotal) return [];
    const map = new Map();
    metodosSource.filter(matchIds).forEach((m) => {
      const key = m.metodo || m.metodo_pago || "Sin metodo";
      const prev = map.get(key) || 0;
      map.set(key, prev + Number(m.value || m.total || 0));
    });
    return Array.from(map.entries()).map(([metodo, value]) => ({ metodo, value }));
  }, [metodosSource, pieTotal, filters.centro_id, filters.curso_id, filters.promocion_id]);

  const barData = useMemo(() => {
    const matchIds = (row) => {
      const matchCentro = !filters.centro_id || String(row.centro_id || "") === String(filters.centro_id);
      const matchCurso = !filters.curso_id || String(row.curso_id || "") === String(filters.curso_id);
      const matchPromo = !filters.promocion_id || String(row.promocion_id || "") === String(filters.promocion_id);
      return matchCentro && matchCurso && matchPromo;
    };
    const map = new Map();
    (cobranzaSource || []).filter(matchIds).forEach((c) => {
      const key = c.dia;
      const prev = map.get(key) || 0;
      map.set(key, prev + Number(c.total || 0));
    });
    const aggregated = Array.from(map.entries())
      .map(([dia, total]) => ({ dia, total }))
      .sort((a, b) => new Date(a.dia) - new Date(b.dia));

    return aggregated.map((c, idx) => ({
      name: formatShortDate(c.dia, idx),
      cobrado: Number(c.total || 0),
      fechaLabel: c.dia ? new Date(c.dia).toLocaleDateString("es-EC") : "",
    }));
  }, [cobranzaSource, filters.centro_id, filters.curso_id, filters.promocion_id]);

  const cobranzaYears = Array.from(
    new Set(
      cobranzaMonthly
        .map((row) => (row.period ? row.period.slice(0, 4) : null))
        .filter(Boolean)
    )
  ).sort((a, b) => b.localeCompare(a));
  const cobranzaMonths = Array.from(
    new Set(
      cobranzaMonthly
        .filter((row) =>
          draftFilters.cobranzaYear === "all"
            ? Boolean(row.period)
            : row.period?.startsWith(`${draftFilters.cobranzaYear}-`)
        )
        .map((row) => (row.period ? row.period.slice(5, 7) : null))
        .filter(Boolean)
    )
  ).sort();

  const metodoYears = Array.from(
    new Set(
      metodoPagoMonthly
        .map((row) => (row.period ? row.period.slice(0, 4) : null))
        .filter(Boolean)
    )
  ).sort((a, b) => b.localeCompare(a));
  const metodoMonths = Array.from(
    new Set(
      metodoPagoMonthly
        .filter((row) =>
          draftFilters.metodoYear === "all"
            ? Boolean(row.period)
            : row.period?.startsWith(`${draftFilters.metodoYear}-`)
        )
        .map((row) => (row.period ? row.period.slice(5, 7) : null))
        .filter(Boolean)
    )
  ).sort();

  const centroOptions = useMemo(() => centros || [], [centros]);
  const cursoOptions = useMemo(() => cursos || [], [cursos]);
  const promoOptions = useMemo(() => promociones || [], [promociones]);

  const pieColors = [
    "#2563eb",
    "#f59e0b",
    "#10b981",
    "#ec4899",
    "#8b5cf6",
    "#14b8a6",
    "#ef4444",
    "#22c55e",
    "#0ea5e9",
  ];
  const cobranzasColor = "#0ea5e9";

  return (
    <>
      <div className="row g-3 admin-kpi-row">
        {kpis.map((kpi) => (
          <div className="col-12 col-sm-6 col-lg-3" key={kpi.label}>
            <div className="admin-kpi-card h-100">
              <div className="admin-kpi-body">
                <span
                  className="admin-kpi-icon"
                  style={{
                    backgroundColor: accentMap[kpi.accent]?.bg || "#e5e7eb",
                    color: accentMap[kpi.accent]?.fg || "#0f172a",
                  }}
                >
                  <i className={`bi ${kpi.icon || "bi-cash-coin"} me-0`} />
                </span>
                <div className="admin-kpi-text">
                  <p className="admin-kpi-label mb-0">{kpi.label}</p>
                  <div className="admin-kpi-value">{formatNumber(kpi.value, kpi.isMoney)}</div>
                  <div className="admin-kpi-helper">{kpi.helper}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mt-3">
        <div className="col-12 col-xl-6">
          <div className="card admin-section-card h-100">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">Cobranza por Dia</h6>
                <small className="text-muted">Montos cobrados por fecha de pago</small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="admin-filter-menu">
                  <button
                    type="button"
                    className="btn btn-filter-chip"
                    onClick={() => toggleDropdown("cobranza")}
                    aria-expanded={openDropdown === "cobranza"}
                  >
                    <i className="bi bi-sliders2 me-1" />
                    Filtros
                    <i className="bi bi-chevron-down ms-1" />
                  </button>
                  {openDropdown === "cobranza" && (
                    <div className="admin-filter-panel">
                      <div className="admin-filter-header">
                        <div>
                          <p className="admin-filter-title">Cobranza por día</p>
                          <small className="text-muted">Filtra por mes y año</small>
                        </div>
                        <button type="button" className="btn btn-light btn-sm" onClick={closeDropdown}>
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Centro</label>
                          <select
                            className="form-select"
                            value={draftFilters.centro_id}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                centro_id: e.target.value,
                                curso_id: "",
                                promocion_id: "",
                              }))
                            }
                          >
                            <option value="">Todos</option>
                            {centroOptions.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.alias || c.nombre || `Centro ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Curso</label>
                          <select
                            className="form-select"
                            value={draftFilters.curso_id}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                curso_id: e.target.value,
                                promocion_id: "",
                              }))
                            }
                          >
                            <option value="">Todos</option>
                            {cursoOptions.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.nombre || `Curso ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Promocion</label>
                          <select
                            className="form-select"
                            value={draftFilters.promocion_id}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                promocion_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">Todas</option>
                            {promoOptions.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.numero_promocion || p.nombre || `Promo ${p.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Año</label>
                          <select
                            className="form-select"
                            value={draftFilters.cobranzaYear}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                cobranzaYear: e.target.value,
                                cobranzaMonth: e.target.value === "all" ? "all" : prev.cobranzaMonth,
                              }))
                            }
                          >
                            <option value="all">Todos</option>
                            {cobranzaYears.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Mes</label>
                          <select
                            className="form-select"
                            value={draftFilters.cobranzaMonth}
                            disabled={draftFilters.cobranzaYear === "all"}
                            onChange={(e) => setDraftFilters((prev) => ({ ...prev, cobranzaMonth: e.target.value }))}
                          >
                            <option value="all">Todos</option>
                            {cobranzaMonths.map((month) => (
                              <option key={month} value={month}>
                                {toMonthLabel(month)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="admin-filter-actions">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetDraftFilters}>
                          Restablecer
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={applyFilters}>
                          Aplicar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <span className="dashboard-chip">
                  <i className="bi bi-bar-chart-line me-1" />
                  {barData.length}
                </span>
              </div>
            </div>
            <div className="card-body chart-body">
              <div className="d-flex justify-content-between align-items-center mb-2 small text-muted">
                {/* <span>Sin metas: solo mostramos lo cobrado</span> */}
                <span>
                  Total periodo: {formatNumber(barData.reduce((a, c) => a + Number(c.cobrado || 0), 0), true)}
                </span>
              </div>
              {barData.length ? (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(v) => formatNumber(v)} />
                      <Tooltip
                        formatter={(value) => [formatNumber(value, true), "Cobrado"]}
                        labelFormatter={(label, payload) => {
                          const row = payload && payload[0] ? payload[0].payload : null;
                          return row && row.fechaLabel ? `${row.fechaLabel}` : label;
                        }}
                      />
                      <Bar dataKey="cobrado" name="Cobrado" fill={cobranzasColor} radius={[6, 6, 0, 0]} maxBarSize={32} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="chart-empty text-muted">Sin datos de cobranza</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card admin-section-card h-100">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">Metodos de Pago</h6>
                <small className="text-muted">Distribucion de ingresos por metodo</small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="admin-filter-menu">
                  <button
                    type="button"
                    className="btn btn-filter-chip"
                    onClick={() => toggleDropdown("metodos")}
                    aria-expanded={openDropdown === "metodos"}
                  >
                    <i className="bi bi-sliders2 me-1" />
                    Filtros
                    <i className="bi bi-chevron-down ms-1" />
                  </button>
                  {openDropdown === "metodos" && (
                    <div className="admin-filter-panel">
                      <div className="admin-filter-header">
                        <div>
                          <p className="admin-filter-title">Metodos de pago</p>
                          <small className="text-muted">Filtra por mes y año</small>
                        </div>
                        <button type="button" className="btn btn-light btn-sm" onClick={closeDropdown}>
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">Centro</label>
                          <select
                            className="form-select"
                            value={draftFilters.centro_id}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                centro_id: e.target.value,
                                curso_id: "",
                                promocion_id: "",
                              }))
                            }
                          >
                            <option value="">Todos</option>
                            {centroOptions.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.alias || c.nombre || `Centro ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Curso</label>
                          <select
                            className="form-select"
                            value={draftFilters.curso_id}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                curso_id: e.target.value,
                                promocion_id: "",
                              }))
                            }
                          >
                            <option value="">Todos</option>
                            {cursoOptions.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.nombre || `Curso ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Promocion</label>
                          <select
                            className="form-select"
                            value={draftFilters.promocion_id}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                promocion_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">Todas</option>
                            {promoOptions.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.numero_promocion || p.nombre || `Promo ${p.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Año</label>
                          <select
                            className="form-select"
                            value={draftFilters.metodoYear}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                metodoYear: e.target.value,
                                metodoMonth: e.target.value === "all" ? "all" : prev.metodoMonth,
                              }))
                            }
                          >
                            <option value="all">Todos</option>
                            {metodoYears.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label">Mes</label>
                          <select
                            className="form-select"
                            value={draftFilters.metodoMonth}
                            disabled={draftFilters.metodoYear === "all"}
                            onChange={(e) => setDraftFilters((prev) => ({ ...prev, metodoMonth: e.target.value }))}
                          >
                            <option value="all">Todos</option>
                            {metodoMonths.map((month) => (
                              <option key={month} value={month}>
                                {toMonthLabel(month)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="admin-filter-actions">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetDraftFilters}>
                          Restablecer
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={applyFilters}>
                          Aplicar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <span className="dashboard-chip">
                  <i className="bi bi-pie-chart me-1" />
                  {pieData.length}
                </span>
              </div>
            </div>
            <div className="card-body chart-body">
              {pieData.length && pieTotal ? (
                <div className="finance-pie-layout">
                  <div className="chart-container-small finance-pie-chart">
                    <ResponsiveContainer width={260} height={260}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="metodo" cx="50%" cy="50%" innerRadius={50} outerRadius={90}>
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} stroke="#ffffff" />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value, true), name || "Metodo"]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="finance-pie-legend d-flex flex-column gap-2 justify-content-center">
                    {pieData.map((p, idx) => {
                      const color = pieColors[idx % pieColors.length];
                      const percent = pieTotal ? Math.round((p.value / pieTotal) * 100) : 0;
                      return (
                        <div
                          key={idx}
                          className="d-flex align-items-center justify-content-between gap-2 small py-1 px-2 rounded"
                          style={{ background: "#f8fafc", border: "1px solid #e5e7eb" }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <span
                              style={{ width: 12, height: 12, background: color, borderRadius: 2, display: "inline-block" }}
                            />
                            <span className="fw-semibold">{p.metodo || "Sin metodo"}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-light text-dark border">{percent}%</span>
                            <span className="fw-semibold">{formatNumber(p.value, true)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="chart-empty text-muted">Sin datos de metodos</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card admin-section-card mt-3">
        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0">Facturas Recientes</h6>
            <small className="text-muted">Estado SRI y pago</small>
          </div>
          <span className="dashboard-chip">
            <i className="bi bi-receipt me-1" />
            {facturas.length}
          </span>
        </div>
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Numero</th>
                <th>Cliente</th>
                <th className="text-end">Total</th>
                <th>Estado SRI</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {facturas.length ? (
                facturas.map((f) => (
                  <tr key={f.id}>
                    <td className="fw-semibold" style={{ fontSize: 12 }}>{f.numero}</td>
                    <td>{f.cliente || '—'}</td>
                    <td className="text-end fw-semibold">{formatNumber(f.total, true)}</td>
                    <td>
                      {f.estado === 'autorizado'
                        ? <span className="badge bg-success">Autorizado</span>
                        : <span className="badge bg-warning text-dark">Sin factura</span>}
                    </td>
                    <td className="text-muted">{f.fecha}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-3">
                    Sin pagos recientes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

